/**
 * Duty validators - Service/duty posting related types
 */
import { Infer, v } from 'convex/values';

// ============================================================================
// Service Type
// ============================================================================

export const serviceTypeValidator = v.union(
  v.literal('fahrdienst'),
  v.literal('sitzdienst'),
  v.literal('kombidienst'),
  v.literal('hintergrunddienst')
);

export type ServiceType = Infer<typeof serviceTypeValidator>;

export const SERVICE_TYPES = [
  'fahrdienst',
  'sitzdienst',
  'kombidienst',
  'hintergrunddienst',
] as const;

// ============================================================================
// Duty Status
// ============================================================================

export const dutyStatusValidator = v.union(
  v.literal('pending_review'),
  v.literal('open'),
  v.literal('pending_payment'),
  v.literal('pending_contract'),
  v.literal('filled'),
  v.literal('performed'),
  v.literal('completed'),
  v.literal('cancelled')
);

export type DutyStatus = Infer<typeof dutyStatusValidator>;

export const DUTY_STATUSES = [
  'pending_review',
  'open',
  'pending_payment',
  'pending_contract',
  'filled',
  'performed',
  'completed',
  'cancelled',
] as const;

// ============================================================================
// Deletion Request Status
// ============================================================================

export const deletionRequestStatusValidator = v.union(
  v.literal('pending'),
  v.literal('approved'),
  v.literal('rejected')
);

export type DeletionRequestStatus = Infer<typeof deletionRequestStatusValidator>;

export const DELETION_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const;

// ============================================================================
// Compensation Type
// ============================================================================

export const compensationTypeValidator = v.union(v.literal('fixed'), v.literal('range'));

export type CompensationType = Infer<typeof compensationTypeValidator>;

export const COMPENSATION_TYPES = ['fixed', 'range'] as const;

// ============================================================================
// Substitute Fee Status
// ============================================================================

export const substituteFeeStatusValidator = v.union(
  v.literal('not_required'),
  v.literal('required'),
  v.literal('pending'),
  v.literal('paid'),
  v.literal('failed')
);

export type SubstituteFeeStatus = Infer<typeof substituteFeeStatusValidator>;

export const SUBSTITUTE_FEE_STATUSES = [
  'not_required',
  'required',
  'pending',
  'paid',
  'failed',
] as const;

// ============================================================================
// Pricing Model (Discriminated Union)
// ============================================================================

export const pricingModelStandardValidator = v.object({
  type: v.literal('standard'),
  commissionRate: v.number(), // 0.075 (7.5%) or 0.05 (5%) based on duration
});

export const pricingModelFastLaneValidator = v.object({
  type: v.literal('fastLane'),
  fee: v.number(), // 8900 (€89 in cents)
});

export const pricingModelValidator = v.union(
  pricingModelStandardValidator,
  pricingModelFastLaneValidator
);

export type PricingModelStandard = Infer<typeof pricingModelStandardValidator>;
export type PricingModelFastLane = Infer<typeof pricingModelFastLaneValidator>;
export type PricingModel = Infer<typeof pricingModelValidator>;

// ============================================================================
// Upsells
// ============================================================================

export const upsellsValidator = v.object({
  betterVisibility: v.boolean(),
  emailBlast: v.boolean(),
});

export type Upsells = Infer<typeof upsellsValidator>;

// ============================================================================
// Probability Tier (for placement probability calculations)
// ============================================================================

export const probabilityTierValidator = v.union(
  v.literal('low'),
  v.literal('medium'),
  v.literal('high')
);

export type ProbabilityTier = Infer<typeof probabilityTierValidator>;

export const PROBABILITY_TIERS = ['low', 'medium', 'high'] as const;
